/**
 * Hook de asignaciones de packs de un cliente (US-2.4, TG-53).
 *
 * Separa **activos** e **histórico** en dos consultas paginadas server-side:
 * - Activos: `GET .../packs?status=ACTIVE&offset&limit`.
 * - Histórico: `GET .../packs?status=ALL&offset&limit`, excluyendo de la vista los
 *   ids que ya viajan en activos para no duplicarlos.
 *
 * Así un cliente con más de una página de asignaciones no pierde packs activos
 * antiguos (el histórico `ALL` los desplazaría). La red vive en `asignacionesApi`;
 * aquí solo el estado de la vista.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ListPackAssignmentsQuery, PageMeta, PackAssignment } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { asignacionesApi } from '../api/asignacionesApi';

/** Asignaciones por página (las dos tablas paginan server-side). */
export const PACK_ASSIGNMENTS_PAGE_SIZE = 20;

type AssignmentStatusFilter = NonNullable<ListPackAssignmentsQuery['status']>;

interface AssignmentPageState {
  data: PackAssignment[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
}

export interface UsePackAssignmentsResult {
  active: PackAssignment[];
  activeMeta: PageMeta;
  activeLoading: boolean;
  activeError: string | null;
  history: PackAssignment[];
  historyMeta: PageMeta;
  historyLoading: boolean;
  historyError: string | null;
  /** Mueve la página de activos (offset server-side). */
  setActivePage: (offset: number) => void;
  /** Mueve la página del histórico (offset server-side). */
  setHistoryPage: (offset: number) => void;
  /** Recarga ambas tablas conservando la página actual. */
  refresh: () => void;
}

const emptyMeta = (limit: number): PageMeta => ({
  offset: 0,
  limit,
  total: 0,
  hasMore: false,
});

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudieron cargar las asignaciones de packs.';
}

/** Carga una página de asignaciones con el filtro de estado indicado. */
function useAssignmentsPage(
  customerId: string | undefined,
  status: AssignmentStatusFilter,
  offset: number,
  reloadKey: number,
): AssignmentPageState {
  const [data, setData] = useState<PackAssignment[]>([]);
  const [meta, setMeta] = useState<PageMeta>(() => emptyMeta(PACK_ASSIGNMENTS_PAGE_SIZE));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) {
      setData([]);
      setMeta(emptyMeta(PACK_ASSIGNMENTS_PAGE_SIZE));
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: ListPackAssignmentsQuery = {
      offset,
      limit: PACK_ASSIGNMENTS_PAGE_SIZE,
      status,
    };

    asignacionesApi
      .listPackAssignments(customerId, params)
      .then((page) => {
        if (cancelled) return;
        setData(page.data);
        setMeta(page.meta);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(caught));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [customerId, status, offset, reloadKey]);

  return { data, meta, loading, error };
}

export function usePackAssignments(
  customerId: string | undefined,
): UsePackAssignmentsResult {
  const [activeOffset, setActiveOffsetState] = useState(0);
  const [historyOffset, setHistoryOffsetState] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  const activePage = useAssignmentsPage(customerId, 'ACTIVE', activeOffset, reloadKey);
  const historyPage = useAssignmentsPage(customerId, 'ALL', historyOffset, reloadKey);

  // El histórico (`ALL`) incluye las activas: se excluyen por id para no duplicarlas.
  const activeIds = useMemo(
    () => new Set(activePage.data.map((assignment) => assignment.id)),
    [activePage.data],
  );
  const history = useMemo(
    () =>
      historyPage.data.filter(
        (assignment) => !activeIds.has(assignment.id) && assignment.status !== 'ACTIVE',
      ),
    [historyPage.data, activeIds],
  );

  useEffect(() => {
    // Al cambiar de cliente se vuelve a la primera página de ambas tablas.
    setActiveOffsetState(0);
    setHistoryOffsetState(0);
  }, [customerId]);

  const setActivePage = useCallback((next: number) => {
    setActiveOffsetState(next < 0 ? 0 : next);
  }, []);

  const setHistoryPage = useCallback((next: number) => {
    setHistoryOffsetState(next < 0 ? 0 : next);
  }, []);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return {
    active: activePage.data.filter((assignment) => assignment.status === 'ACTIVE'),
    activeMeta: activePage.meta,
    activeLoading: activePage.loading,
    activeError: activePage.error,
    history,
    historyMeta: historyPage.meta,
    historyLoading: historyPage.loading,
    historyError: historyPage.error,
    setActivePage,
    setHistoryPage,
    refresh,
  };
}
